import { object, string } from "yup";
import { useFormik } from "formik";
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  TextField,
} from "@mui/material";
import { useIdentity } from "../IdentityProvider";
import { APINotificationDismissalReason } from "../../types/APINotificationDismissalReason";
import { useTrueBizApi } from "../../api";

const NotificationDismissalFormSchema = object({
  reason: string()
    .oneOf(Object.keys(APINotificationDismissalReason))
    .required(),
  notes: string().optional().nullable(),
});
type FormState = ReturnType<
  typeof NotificationDismissalFormSchema.validateSync
>;

export default function NotificationDismisallForm({
  notificationId,
  open,
  onClose,
  onSubmit,
}: {
  notificationId: string;
  open: boolean;
  onClose: () => void;
  onSubmit: () => void;
}) {
  const identity = useIdentity();
  const api = useTrueBizApi();
  const formik = useFormik({
    initialValues: {
      reason: "WARNNOTICE",
      notes: "",
    },
    onSubmit: (values: FormState) => {
      if (!identity.isAuthenticated) return;
      const formValues = {
        ...values,
        id: notificationId,
        notes: values.notes ? values.notes : null,
        // @ts-ignore Typescript doesn't like this but I don't know an alternate way to do it
        reason: APINotificationDismissalReason[values.reason],
      };
      return api
        .acknowledgeNotification(
          formValues.id,
          formValues.reason,
          formValues.notes
        )
        .then(
          () => {
            onSubmit();
            formik.setSubmitting(false);
          },
          (error) => {
            console.error(error);
            return error.json().then((errorJson: any) => {
              formik.setStatus(
                `The following error ocurred: ${errorJson.message}`
              );
            });
          }
        );
    },
    validationSchema: NotificationDismissalFormSchema,
  });

  return (
    <Dialog open={open} key={notificationId}>
      <DialogTitle>Dismiss Monitoring Issue</DialogTitle>
      <DialogContent>
        <Grid container spacing={2} mt={3} pl={2} pr={2} minWidth="400px">
          <form onSubmit={formik.handleSubmit} style={{ width: "100%" }}>
            {formik.status && (
              <Alert severity="error" sx={{ mt: 1, mb: 1 }}>
                {formik.status}
              </Alert>
            )}

            <Grid item xs={12} mt={3}>
              <FormControl sx={{ width: "100%" }}>
                <InputLabel id="reason-label">Reason</InputLabel>
                <Select
                  labelId="reason-label"
                  label="Reason"
                  id="reason-select"
                  onChange={formik.handleChange}
                  value={formik.values.reason}
                  name="reason"
                >
                  {Object.entries(APINotificationDismissalReason).map(
                    ([key, value]) => {
                      return (
                        <MenuItem value={key} key={`${notificationId}-${key}`}>
                          {value}
                        </MenuItem>
                      );
                    }
                  )}
                </Select>
                {formik.errors.reason && formik.touched.reason && (
                  <Alert severity="error" sx={{ mt: 1, mb: 1 }}>
                    {formik.errors.reason}
                  </Alert>
                )}
              </FormControl>
            </Grid>

            <Grid item xs={12} mt={3}>
              <TextField
                fullWidth
                type="text"
                multiline
                rows={4}
                inputProps={{ maxLength: 10485760 }}
                label="Notes"
                name="notes"
                onChange={formik.handleChange}
                value={formik.values.notes}
              />
            </Grid>

            <Grid item xs={12} mt={3}>
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "row",
                  justifyContent: "space-between",
                  width: "100%",
                }}
              >
                <Box sx={{ display: "flex", flexDirection: "row" }}>
                  <Button
                    variant="outlined"
                    size="large"
                    onClick={() => {
                      formik.setStatus(undefined);
                      formik.resetForm(); // Hopefully unnecessary when there's a real backend
                      onClose();
                    }}
                    // TODO: Disable on cancel when there's a real backend...
                  >
                    Cancel
                  </Button>
                </Box>

                <Box sx={{ display: "flex" }}>
                  <Button
                    variant="contained"
                    disabled={formik.isSubmitting}
                    type="submit"
                  >
                    Submit
                  </Button>
                </Box>
              </Box>
            </Grid>
          </form>
        </Grid>
      </DialogContent>
    </Dialog>
  );
}
